import { useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createAudio } from '../../../service/AudioService';
import type { PlayAudioCallbacks } from '../../../interface/PlayAudio';
import { changeLoadingTimes } from '../../../redux/appConfigSlice';
import { loadingThresholdValues } from '../../../constants/constants';
import { canParseURL } from '../../../service/utils';

export const useAudioPlayer = () => {
    const dispatch = useDispatch();
    const isPausedRef = useRef(false);
    const isPlayingRef = useRef(false);
    const playErrorRef = useRef(false);

    const audioRef = useRef<HTMLAudioElement>(createAudio());
    const audioStartTime = useRef<number>(0);
    const audioLoadingTime = useRef<number>(0);
    const audioLoadingList = useRef<Array<{ [key: string]: string }>>([]);

    useEffect(() => {
        return () => {
            audioRef.current.pause();
            audioRef.current.src = '';
        };
    }, []);

    const playAudio = (url: string, callbacks: PlayAudioCallbacks) => {
        const { onPlay, onEnded, onError, force, onCanPlayThrough } = callbacks;

        if ((isPlayingRef.current || isPausedRef.current) && !force) return;

        const audio = audioRef.current;
        audio.src = url;

        audio.onplay = () => {
            playErrorRef.current = false;
            isPlayingRef.current = true;

            audioStartTime.current = Date.now();

            console.log('Playing audio started');
            onPlay?.();
        };
        audio.oncanplaythrough = () => {
            let audioEndTime = Date.now();
            audioLoadingTime.current = (audioEndTime - audioStartTime.current) / 1000; // in seconds
            let isURL = canParseURL(url);
            let path: string = '';
            if (isURL) {
                const urlObject = new URL(url);
                path = urlObject.pathname;
            } else {
                path = url;
            }
            audioLoadingList.current = [...audioLoadingList.current, { [path]: `${audioLoadingTime.current}s` }];
            onCanPlayThrough?.();
            dispatch(changeLoadingTimes({ audiofilesLoadingTime: audioLoadingList.current }));
            if (audioLoadingTime.current > loadingThresholdValues.audioLoadingTime) {
                console.error(
                    `Audio loading time ${audioLoadingTime.current}s exceeded ${loadingThresholdValues.audioLoadingTime} threshold`,
                );
            }
        };

        audio.play().catch((error) => {
            playErrorRef.current = true;
            isPlayingRef.current = false;

            console.error('Error playing audio URL:', error);
            onError?.(error);
        });

        audio.onended = () => {
            playErrorRef.current = false;
            console.log('Audio has ended');
            onEnded?.();
            isPlayingRef.current = false; // important this assignment happens after onEnd callback
        };

        audio.onerror = (error) => {
            isPlayingRef.current = false;
            console.error('Error while playing audio URL:', error);
            onError?.(error);
        };
    };

    const pause = (force?: boolean) => {
        if (!isPlayingRef.current && !force) return;

        audioRef.current.pause();

        isPausedRef.current = true;

        console.log('Audio has been paused');
    };

    const resume = async () => {
        if (!isPausedRef.current) return;

        if (playErrorRef.current) {
            audioRef.current.load();
        }

        await audioRef.current.play();

        isPausedRef.current = false;

        console.log('Audio has been resumed');
    };

    return { playAudio, isPlayingRef, audioRef, pause, resume, isPausedRef };
};
